import { ProviderType } from '../../../../../app/Slices/SevenDayApiTypes';
import { camelCaseToTitleCase } from '../../../../../shared/utils';
import { ServiceType } from '../../../../enums/contractor';
import { ItineraryItemType } from '../../../../enums/itinerary';
import { SetMenuCourse } from '../../../../enums/set-menu';
import {
    ItineraryItemDto,
    ItineraryItemLinkedBookingInfo,
    LatLng,
} from '../../../../types/itinerary';
import { SetMenuItem } from '../../../../types/set-menu';

const SET_MENU_COURSE_ORDER: { value: SetMenuCourse; label: string }[] = [
    { value: SetMenuCourse.Starter, label: 'Starters' },
    { value: SetMenuCourse.Main, label: 'Mains' },
    { value: SetMenuCourse.Side, label: 'Sides' },
    { value: SetMenuCourse.Dessert, label: 'Desserts' },
    { value: SetMenuCourse.Drink, label: 'Drinks' },
];

export type ItineraryItemStatusColor =
    | 'green'
    | 'red'
    | 'yellow'
    | 'gray'
    | 'blue';

export const getItineraryItemStatusColor = (
    status: string,
): ItineraryItemStatusColor => {
    if (['completed', 'active'].includes(status)) {
        return 'green';
    }
    if (status === 'cancelled') {
        return 'red';
    }
    if (['approved', 'expired'].includes(status)) {
        return 'yellow';
    }
    if (status === 'declined') {
        return 'gray';
    }
    return 'blue';
};

export const getTitle = (item: ItineraryItemDto): string => {
    if (item.isMainReservation) {
        return (item.itineraryItemInfo as ItineraryItemLinkedBookingInfo).name;
    }
    const info = item.itineraryItemInfo;
    switch (info.itemType) {
        case ItineraryItemType.Flight:
            return info.airline
                ? `${info.airline} ${info.flightNumber ?? ''}`.trim()
                : 'Flight';
        case ItineraryItemType.Event:
            return info.title;
        case ItineraryItemType.Transfer:
            return info.provider ? `Transfer – ${info.provider}` : 'Transfer';
        case ItineraryItemType.Restaurant:
            return info.restaurantName;
        case ItineraryItemType.Custom:
            return info.title;
        case ItineraryItemType.Property:
        case ItineraryItemType.Commercial:
        case ItineraryItemType.Vehicle:
        case ItineraryItemType.Boat:
        case ItineraryItemType.Activity:
            return camelCaseToTitleCase(info.subType);
        case ItineraryItemType.PartnerService:
            return info.providerType === ProviderType.Contractor
                ? `${camelCaseToTitleCase(info.serviceType === ServiceType.Other ? 'Service' : info.serviceType)} by ${info.contractor.name}`
                : `${camelCaseToTitleCase(info.serviceType)} by the host`;
        default:
            return 'Itinerary Item';
    }
};

export const haversineDistanceKm = (a: LatLng, b: LatLng): number => {
    const R = 6371;
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const s =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(a.lat)) *
            Math.cos(toRad(b.lat)) *
            Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
};

export const geoMidpoint = (a: LatLng, b: LatLng): LatLng => ({
    lat: (a.lat + b.lat) / 2,
    lng: (a.lng + b.lng) / 2,
});

export const estimateFlightDuration = (a: LatLng, b: LatLng): string => {
    const km = haversineDistanceKm(a, b);
    const TAXI_TAKEOFF_LANDING_MIN = 40;
    const CRUISE_SPEED_KMH = 800;
    const totalMin =
        TAXI_TAKEOFF_LANDING_MIN + Math.round((km / CRUISE_SPEED_KMH) * 60);
    if (totalMin < 60) return `~${totalMin} min`;
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m > 0 ? `~${h}h ${m}m` : `~${h}h`;
};

export const pathMidpoint = (path: LatLng[]): LatLng | null => {
    if (!path.length) return null;
    if (path.length === 1) return path[0];
    const toRad = (deg: number) => (deg * Math.PI) / 180;
    const haversine = (a: LatLng, b: LatLng) => {
        const R = 6371;
        const dLat = toRad(b.lat - a.lat);
        const dLng = toRad(b.lng - a.lng);
        const s =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(a.lat)) *
                Math.cos(toRad(b.lat)) *
                Math.sin(dLng / 2) ** 2;
        return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
    };

    let totalDist = 0;
    for (let i = 0; i < path.length - 1; i++)
        totalDist += haversine(path[i], path[i + 1]);
    if (totalDist === 0) return path[0];

    const halfDist = totalDist / 2;
    let accumulated = 0;
    for (let i = 0; i < path.length - 1; i++) {
        const segDist = haversine(path[i], path[i + 1]);
        if (accumulated + segDist >= halfDist) {
            const f = segDist > 0 ? (halfDist - accumulated) / segDist : 0;
            return {
                lat: path[i].lat + (path[i + 1].lat - path[i].lat) * f,
                lng: path[i].lng + (path[i + 1].lng - path[i].lng) * f,
            };
        }
        accumulated += segDist;
    }
    return path[path.length - 1];
};

export const groupSetMenuItemsByCourse = (items: SetMenuItem[]) =>
    SET_MENU_COURSE_ORDER.map((course) => ({
        ...course,
        items: items
            .filter((item) => item.course === course.value)
            .sort((a, b) => a.order - b.order),
    })).filter((group) => group.items.length > 0);

export const hasSelectedItems = (item: ItineraryItemDto): boolean => {
    const info = item.itineraryItemInfo;
    if (
        info.itemType === ItineraryItemType.PartnerService &&
        info.providerType === ProviderType.Contractor
    ) {
        return info.items.length > 0;
    }
    if (
        info.itemType === ItineraryItemType.PartnerService &&
        info.providerType === ProviderType.Owner
    ) {
        return info.setMenu.items.length > 0;
    }
    return false;
};
